import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import { fetchPublicModelService, publicModelPreviewUrl } from "../../services/publicModelsService";
import { useAuthContext } from "../../context/AuthProvider";
import { STORAGE } from "../../utils/headers";
import { genderLabel } from "../../utils/labels";
import PublicLayout from "../../layouts/PublicLayout";
import ProfileGallery from "../../components/components-model/ProfileGallery";
import ProfileCover from "../../components/components-model/ProfileCover";
import noimg from "../../assets/img/noimg.jpg";
import "./public-models.css";

const asset = (path) => (path ? `${STORAGE}/${path}` : noimg);

/**
 * Perfil público de una modelo.
 *
 * Es accesible sin sesión y sin pago: muestra fotos, videos cortos y textos de
 * presentación. El botón hacia el show en vivo lleva al registro cuando el
 * visitante todavía no tiene cuenta.
 */
export default function ModelProfile() {
    const { slug } = useParams();
    const { user } = useAuthContext();
    const params = useMemo(() => ({ slug }), [slug]);
    const { data: model, loading, error } = useFetch(fetchPublicModelService, { params });

    const [lightbox, setLightbox] = useState(null);
    const [tab, setTab] = useState("photos");

    // Descripción corta y estable para buscadores y previsualización de enlaces.
    const metaDescription = useMemo(() => {
        if (!model) return undefined;
        const facts = [model.city, model.country, model.age && `${model.age} años`]
            .filter(Boolean)
            .join(" · ");
        const intro = model.tagline || model.description || `Conoce el perfil de ${model.name} en Afrodita Models.`;
        return `${intro}${facts ? ` (${facts})` : ""}`.slice(0, 300);
    }, [model]);

    const jsonLd = useMemo(() => {
        if (!model) return null;
        return {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "name": model.name,
            "url": window.location.href,
            "description": metaDescription,
            "mainEntity": {
                "@type": "Person",
                "name": model.name,
                "description": model.description || model.tagline || undefined,
                "image": model.img ? asset(model.img) : undefined,
                "knowsLanguage": model.languages || undefined,
                "homeLocation": model.city || model.country
                    ? {
                        "@type": "Place",
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": model.city || undefined,
                            "addressCountry": model.country || undefined,
                        },
                    }
                    : undefined,
            },
        };
    }, [model, metaDescription]);

    useDocumentMeta({
        title: model?.name,
        description: metaDescription,
        image: model?.img ? asset(model.img) : undefined,
        type: "profile",
        jsonLd,
    });

    const photos = useMemo(
        () =>
            (model?.media?.filter((item) => item.type === "image") ?? []).map((photo) => ({
                ...photo,
                src: asset(photo.path),
                alt: photo.title || `Foto de ${model?.name ?? "la modelo"}`,
            })),
        [model]
    );
    const videos = useMemo(
        () => model?.media?.filter((item) => item.type === "video") ?? [],
        [model]
    );

    if (loading && !model) {
        return (
            <PublicLayout>
                <div className="public-profile-loading">
                    <div className="spinner-border text-warning" role="status"></div>
                    <p>Cargando perfil...</p>
                </div>
            </PublicLayout>
        );
    }

    if (error || !model) {
        return (
            <PublicLayout>
                <div className="public-empty py-5">
                    <i className="fa-solid fa-user-slash"></i>
                    <h2>Perfil no disponible</h2>
                    <p>{error || "Este perfil no existe o fue retirado."}</p>
                    <Link to="/modelos" className="public-btn public-btn-ghost">
                        Ver todas las modelos
                    </Link>
                </div>
            </PublicLayout>
        );
    }

    // Sin sesión el visitante pasa por el registro y vuelve a la sala en vivo.
    const liveTarget = `/live/${model.id}`;
    const liveLink = user
        ? liveTarget
        : `/register?next=${encodeURIComponent(liveTarget)}`;

    return (
        <PublicLayout>
            <article className="public-profile">
                <ProfileCover
                    cover={model.cover_img ? asset(model.cover_img) : null}
                    photos={photos}
                    name={model.name}
                >
                    <div className="public-profile-hero-inner">
                            {/* Sin foto se muestra la inicial sobre fondo oscuro: el
                                marcador blanco por defecto desentonaba sobre la portada. */}
                            {model.img ? (
                                <img
                                    className="public-profile-avatar"
                                    src={asset(model.img)}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = noimg;
                                    }}
                                    alt={model.name}
                                    width="160"
                                    height="160"
                                />
                            ) : (
                                <div
                                    className="public-profile-avatar public-profile-avatar-initial"
                                    aria-label={model.name}
                                >
                                    {model.name?.[0]?.toUpperCase() || 'A'}
                                </div>
                            )}
                            <div className="public-profile-identity">
                                <h1 className="public-profile-name">
                                    {model.name}
                                    {model.is_live && (
                                        <span className="public-live-badge ms-3">
                                            <span className="pulse-dot"></span> EN VIVO
                                        </span>
                                    )}
                                </h1>
                                {model.tagline && (
                                    <p className="public-profile-tagline">{model.tagline}</p>
                                )}
                                <ul className="public-profile-facts">
                                    {model.age && (
                                        <li>
                                            <i className="fa-solid fa-cake-candles"></i> {model.age} años
                                        </li>
                                    )}
                                    {model.gender && (
                                        <li>
                                            <i className="fa-solid fa-venus-mars"></i> {genderLabel(model.gender)}
                                        </li>
                                    )}
                                    {(model.city || model.country) && (
                                        <li>
                                            <i className="fa-solid fa-location-dot"></i>{" "}
                                            {[model.city, model.country].filter(Boolean).join(", ")}
                                        </li>
                                    )}
                                    {model.languages && (
                                        <li>
                                            <i className="fa-solid fa-language"></i> {model.languages}
                                        </li>
                                    )}
                                </ul>

                                <div className="public-profile-actions">
                                    <Link to={liveLink} className="public-btn public-btn-primary">
                                        <i className="fa-solid fa-video"></i>
                                        {model.is_live ? "Entrar al show en vivo" : "Ver sala en vivo"}
                                    </Link>
                                    {!user && (
                                        <Link
                                            to={`/login?next=${encodeURIComponent(liveTarget)}`}
                                            className="public-btn public-btn-ghost"
                                        >
                                            Ya tengo cuenta
                                        </Link>
                                    )}
                                </div>
                                {!user && (
                                    <p className="public-profile-note">
                                        El perfil y su galería son gratuitos. El chat y la transmisión
                                        en vivo requieren cuenta y tokens.
                                    </p>
                                )}
                            </div>

                            {model.is_live && (
                                <div className="public-profile-preview">
                                    <img
                                        src={publicModelPreviewUrl(model.slug)}
                                        onError={(e) => {
                                            e.target.style.display = "none";
                                        }}
                                        alt={`Vista previa de ${model.name}`}
                                        width="320"
                                        height="180"
                                    />
                                    <span>Vista previa de la transmisión</span>
                                </div>
                            )}
                    </div>
                </ProfileCover>

                <div className="container public-profile-body">
                    <div className="row g-4">
                        <div className="col-lg-8">
                            <div className="public-tabs" role="tablist">
                                <button
                                    role="tab"
                                    aria-selected={tab === "photos"}
                                    className={tab === "photos" ? "active" : ""}
                                    onClick={() => setTab("photos")}
                                >
                                    Fotos ({photos.length})
                                </button>
                                <button
                                    role="tab"
                                    aria-selected={tab === "videos"}
                                    className={tab === "videos" ? "active" : ""}
                                    onClick={() => setTab("videos")}
                                >
                                    Videos ({videos.length})
                                </button>
                            </div>

                            {tab === "photos" && (
                                <ProfileGallery
                                    photos={photos}
                                    name={model.name}
                                    onOpen={setLightbox}
                                />
                            )}

                            {tab === "videos" && (
                                <div className="public-videos">
                                    {videos.length === 0 && (
                                        <p className="public-muted">
                                            Esta modelo aún no ha publicado videos.
                                        </p>
                                    )}
                                    {videos.map((video) => (
                                        <figure className="public-video-item" key={video.id}>
                                            <video
                                                controls
                                                preload="none"
                                                playsInline
                                                poster={video.thumbnail ? asset(video.thumbnail) : undefined}
                                            >
                                                <source src={asset(video.path)} />
                                                Tu navegador no puede reproducir este video.
                                            </video>
                                            {(video.title || video.description) && (
                                                <figcaption>
                                                    {video.title && <strong>{video.title}</strong>}
                                                    {video.description && <p>{video.description}</p>}
                                                </figcaption>
                                            )}
                                        </figure>
                                    ))}
                                </div>
                            )}
                        </div>

                        <aside className="col-lg-4">
                            <section className="public-panel">
                                <h2>Sobre mí</h2>
                                <p>
                                    {model.description ||
                                        "Esta modelo todavía no ha escrito su presentación."}
                                </p>
                            </section>

                            {model.preferences && (
                                <section className="public-panel">
                                    <h2>Mis preferencias</h2>
                                    <p className="public-preferences">{model.preferences}</p>
                                </section>
                            )}

                            <section className="public-panel public-panel-cta">
                                <h2>¿Quieres verla en vivo?</h2>
                                <p>
                                    Crea tu cuenta gratis, recarga tokens y entra a su sala privada
                                    con chat en tiempo real y propinas.
                                </p>
                                <Link to={liveLink} className="public-btn public-btn-primary w-100">
                                    {user ? "Ir a la sala" : "Crear cuenta y entrar"}
                                </Link>
                            </section>
                        </aside>
                    </div>
                </div>
            </article>

            {lightbox && (
                <div
                    className="public-lightbox"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setLightbox(null)}
                >
                    <button className="public-lightbox-close" aria-label="Cerrar">
                        <i className="fa fa-times"></i>
                    </button>
                    <img
                        src={asset(lightbox.path)}
                        alt={lightbox.title || model.name}
                        onClick={(e) => e.stopPropagation()}
                    />
                    {lightbox.description && <p>{lightbox.description}</p>}
                </div>
            )}
        </PublicLayout>
    );
}
